import * as Sentry from "@sentry/react";
import type { AnyRouter } from "@tanstack/react-router";

const parseRate = (value: string | undefined, fallback: number): number => {
	if (!value) {
		return fallback;
	}

	const parsed = Number.parseFloat(value);
	return Number.isFinite(parsed) ? parsed : fallback;
};

let initialized = false;

export const initSentry = (router: AnyRouter): void => {
	if (initialized) {
		return;
	}

	const dsn = import.meta.env.VITE_SENTRY_DSN;

	if (!dsn) {
		return;
	}

	const environment =
		import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE;
	const release = import.meta.env.VITE_SENTRY_RELEASE;

	const tracesSampleRate = parseRate(
		import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
		import.meta.env.DEV ? 1 : 0.2,
	);

	Sentry.init({
		dsn,
		// Sentry 11 collects user info, cookies, headers, bodies and DB query
		// text by default. Keep v10's `sendDefaultPii: false` data set.
		dataCollection: {
			userInfo: false,
			cookies: false,
			httpHeaders: false,
			httpBodies: [],
			urlQueryParams: false,
			databaseQueryData: false,
			genAI: { inputs: false, outputs: false },
			stackFrameVariables: false,
		},
		environment,
		release,
		tracesSampleRate,
		integrations: [
			Sentry.tanstackRouterBrowserTracingIntegration(router, {
				enableInp: true,
			}),
		],
		beforeSend(event) {
			if (event.request?.headers) {
				const sanitizedHeaders: Record<string, string> = {};

				for (const [key, value] of Object.entries(event.request.headers)) {
					const lower = key.toLowerCase();
					if (lower === "authorization" || lower === "cookie") {
						continue;
					}
					sanitizedHeaders[key] = value;
				}

				event.request.headers = sanitizedHeaders;
			}

			return event;
		},
	});

	initialized = true;
};
